import { useMemo, useState, type ReactNode } from 'react';
import { Search, Settings2 } from 'lucide-react';
import { Input, cn } from '@cyphex/ui';
import { CustomEmojiSection } from '@/features/emoji';
import { EMOJI_GROUPS, searchEmoji, type EmojiGroup } from '../../data/emoji';
import { PickerPopover } from './PickerPopover';

interface EmojiPickerProps {
  trigger: ReactNode;
  /** Receives a unicode character, or a `:shortcode:` for a workspace emoji. */
  onSelect: (emoji: string) => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  align?: 'start' | 'end';
  /** Opens the workspace emoji manager. Omitted where managing makes no sense. */
  onManageEmoji?: () => void;
}

/**
 * Searchable emoji picker built on the built-in set — no dataset download.
 *
 * The workspace's own emoji render ABOVE the unicode groups: they are what
 * people are actually hunting for, and a custom emoji buried under 1,800 faces
 * is a custom emoji nobody uses.
 */
export function EmojiPicker({
  trigger,
  onSelect,
  open,
  onOpenChange,
  align = 'start',
  onManageEmoji,
}: EmojiPickerProps) {
  const [query, setQuery] = useState('');
  const results = useMemo(() => searchEmoji(query), [query]);

  const grouped = useMemo(() => {
    const map = new Map<EmojiGroup, string[]>();
    results.forEach((entry) => {
      const list = map.get(entry.group) ?? [];
      list.push(entry.char);
      map.set(entry.group, list);
    });
    return map;
  }, [results]);

  return (
    <PickerPopover
      trigger={trigger}
      open={open}
      onOpenChange={onOpenChange}
      align={align}
      label="Pick an emoji"
    >
      <Input
        variant="search"
        size="sm"
        hideLabel
        label="Search emoji"
        leadingIcon={Search}
        placeholder="Search emoji"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        autoFocus
      />

      <div className="scrollbar-quiet mt-2 max-h-64 overflow-y-auto pr-1">
        <CustomEmojiSection
          query={query}
          onSelect={onSelect}
          footer={
            onManageEmoji === undefined ? undefined : (
              <button
                type="button"
                onClick={onManageEmoji}
                className={cn(
                  'mt-1 flex w-full items-center gap-1.5 rounded-md px-1 py-1 text-2xs',
                  'text-muted-foreground transition-colors duration-fast hover:bg-subtle hover:text-foreground',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                )}
              >
                <Settings2 size={12} aria-hidden />
                Manage workspace emoji
              </button>
            )
          }
        />

        {EMOJI_GROUPS.filter((group) => (grouped.get(group)?.length ?? 0) > 0).map((group) => (
          <section key={group} className="mb-2">
            <h3 className="mb-1 px-1 text-2xs font-semibold uppercase tracking-wide text-muted-foreground">
              {group}
            </h3>
            <div className="grid grid-cols-8 gap-0.5">
              {(grouped.get(group) ?? []).map((char) => (
                <button
                  key={char}
                  type="button"
                  onClick={() => onSelect(char)}
                  aria-label={`React with ${char}`}
                  className={cn(
                    'flex h-8 w-8 items-center justify-center rounded-md text-lg transition-colors duration-fast',
                    'hover:bg-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  )}
                >
                  {char}
                </button>
              ))}
            </div>
          </section>
        ))}
        {results.length === 0 ? (
          <p className="px-1 py-6 text-center text-sm text-muted-foreground">No emoji found.</p>
        ) : null}
      </div>
    </PickerPopover>
  );
}
